import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatTimestampInTimezone, errorWords } from "@alm/domain";
import { SUPPORT_CATEGORY_LABELS, type SupportCategory } from "@alm/validation";
import { FactStrip } from "@/components/app/patterns/fact-strip";
import { RecordHeader } from "@/components/app/patterns/record-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { useZone } from "@/lib/facility/zone";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/_support/help/$requestId")({
  component: HelpRequestPage,
});

function HelpRequestPage() {
  const { requestId } = Route.useParams();
  const zone = useZone();
  const request = useQuery(orpc.support.requests.get.queryOptions({ input: { requestId } }));
  const queryClient = useQueryClient();
  const close = useMutation(
    orpc.support.requests.close.mutationOptions({
      onSuccess: async (saved) => {
        toast.success(saved.status === "resolved" ? "Request closed" : "Request reopened");
        await queryClient.invalidateQueries({ queryKey: orpc.support.requests.key() });
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  const back = (
    <Link
      to="/help"
      className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm"
    >
      <ArrowLeft className="size-4" /> My requests
    </Link>
  );
  if (request.isPending)
    return (
      <>
        {back}
        <Skeleton className="h-48 w-full" />
      </>
    );
  if (request.isError) {
    return (
      <>
        {back}
        <ReadFailed
          message="Couldn't load this request."
          error={request.error}
          onRetry={() => void request.refetch()}
          retrying={request.isRefetching}
        />
      </>
    );
  }

  const data = request.data;
  const resolved = data.status === "resolved";
  const facts = [
    {
      label: "About",
      value: SUPPORT_CATEGORY_LABELS[data.category as SupportCategory] ?? data.category,
    },
    { label: "Sent", value: formatTimestampInTimezone(data.createdAt, zone) },
    {
      label: "Resolved",
      value: data.resolvedAt ? formatTimestampInTimezone(data.resolvedAt, zone) : "Not yet",
    },
    { label: "Sent from", value: data.page ?? "Not recorded", wrap: true },
  ];

  return (
    <>
      <RecordHeader
        back={{ label: "My requests", link: { to: "/help" } }}
        title={data.reference}
        badges={<Badge variant={resolved ? "ok" : "info"}>{resolved ? "Resolved" : "Open"}</Badge>}
        actions={
          <Button
            variant="outline"
            disabled={close.isPending}
            onClick={() => close.mutate({ requestId, closed: !resolved })}
          >
            {resolved ? "Reopen request" : "Close request"}
          </Button>
        }
        strip={<FactStrip facts={facts} />}
      />
      <Card>
        <CardHeader>
          <CardTitle>Your message</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm break-words whitespace-pre-line">{data.message}</p>
          {data.screenshotUrl ? (
            <a href={data.screenshotUrl} target="_blank" rel="noreferrer" className="block w-fit">
              <img
                src={data.screenshotUrl}
                alt={`Screenshot attached to ${data.reference}`}
                className="max-h-96 rounded-md border"
              />
            </a>
          ) : null}
          <p className="text-muted-foreground text-xs">
            {!resolved
              ? "It's with the team. We reply by email; close it here once you're sorted."
              : data.closedByYou
                ? "You closed this. Reopen it if the problem comes back."
                : "We've marked this resolved. Reopen it if it isn't."}
          </p>
        </CardContent>
      </Card>
    </>
  );
}
